import { api } from '@/api/client';

export interface TransactionInvoiceItem {
  transaction_id: number | string;
  transaction_amount: number | string;
  transaction_date: string;
}

export interface InvoiceMetadata {
  date_to?: string;
  date_from?: string;
  line_items?: {
    total_commission?: string;
    total_transaction_amount?: string;
  };
  resend_count?: number;
  generated_via?: string;
  transactions_count?: number;
  [key: string]: unknown;
}

export interface InvoiceItem {
  invoice_number: string;
  invoice_note?: string;
  invoice_initiated_date?: string;
  invoice_settled_date?: string;
  invoice_amount: number | string;
  invoice_status: string;
  invoice_expiry_date?: string;
  invoice_url?: string;
  mode_of_payment?: string | null;
  utr?: string | null;
  bank_name?: string | null;
  ifsc?: string | null;
  payer_name?: string | null;
  metadata?: InvoiceMetadata | null;
  transaction_invoice?: TransactionInvoiceItem[];
}

export interface InvoiceListParams {
  start_date?: string;
  end_date?: string;
  invoice_status?: string;
  page?: number;
  page_size?: number;
}

export interface InvoicePagination {
  page: number;
  page_size: number;
  total_pages: number;
  total_items: number;
}

export interface InvoiceListResponse {
  success?: boolean;
  results?: InvoiceItem[];
  data?: InvoiceItem[];
  pagination?: InvoicePagination;
}

export interface GenerateInvoiceRequest {
  merchant_uuid: string;
  date_from: string;
  date_to: string;
}

export interface GenerateInvoiceResponse {
  success: boolean;
  message: string;
  invoice_id: string;
  data: {
    invoice_id: string;
    invoice_number: string;
  };
}

export interface DownloadInvoiceResponse {
  success: boolean;
  download_link: string;
}

export interface ResendInvoiceRequest {
  invoice_id: string;
}

export interface ResendInvoiceResponse {
  success: boolean;
  message: string;
}

const INVOICE_ENDPOINTS = {
  LIST: '/merchant/v1/invoices/',
  DOWNLOAD_PREFIX: '/merchant/v1/download-invoice/',
  GENERATE: '/backoffice/api/v1/generate-invoice/',
  RESEND: '/backoffice/api/v1/resend-invoice/',
} as const;

class InvoiceService {
  /**
   * Fetch list of invoices
   */
  async list(params: InvoiceListParams = {}): Promise<InvoiceListResponse> {
    const response = await api.get<InvoiceListResponse>(INVOICE_ENDPOINTS.LIST, {
      params,
    });
    return response as unknown as InvoiceListResponse;
  }

  /**
   * Download an invoice by invoice_id
   * Returns a download link to the PDF
   */
  async download(invoiceUuid: string): Promise<DownloadInvoiceResponse> {
    const response = await api.get<DownloadInvoiceResponse>(INVOICE_ENDPOINTS.DOWNLOAD_PREFIX, {
      params: { invoice_id: invoiceUuid }
    });
    return response as unknown as DownloadInvoiceResponse;
  }

  /**
   * Generate a new invoice for a merchant within a date range
   */
  async generate(data: GenerateInvoiceRequest): Promise<GenerateInvoiceResponse> {
    const response = await api.post<GenerateInvoiceResponse>(INVOICE_ENDPOINTS.GENERATE, data);
    return response as unknown as GenerateInvoiceResponse;
  }

  /**
   * Resend an invoice by invoice_id
   */
  async resend(invoiceId: string): Promise<ResendInvoiceResponse> {
    const response = await api.post<ResendInvoiceResponse>(INVOICE_ENDPOINTS.RESEND, {
      invoice_id: invoiceId,
    });
    return response as unknown as ResendInvoiceResponse;
  }
}

export const invoiceService = new InvoiceService();
